/**
 * Whether visitors can actually experience BOTH light and dark on the public
 * site — derived from Theme Design's Default Theme + "Allow visitors to
 * switch themes" fields (`ThemeSiteDesign.colors.defaultMode` /
 * `.allowVisitorThemeSwitcher`, see `apps/tenant-dashboard/src/lib/schemas/theme-site-design.ts`).
 * This is the single source of truth gating:
 * - The builder's canvas toolbar Sun/Moon (`BuilderCanvasModeContext`) and
 *   `ColorModeField`'s field-side toggle (`.cursor/docs/builder/COLOR_MODE_VALUE.md`).
 *   The Theme tab's kit swatch list is deliberately NOT gated by this — see
 *   that doc for why (picking a Theme token stores the same mode-agnostic
 *   `var(--tt-color-*)` reference regardless of which kit you browsed).
 * - The native `theme_switcher` element (`VisitorThemeSwitcherControl.tsx`):
 *   whether the builder palette/canvas show it enabled vs. disabled-with-hint,
 *   and whether the public Region Instance renders a real, working control at
 *   all (`RenderOptions.visitorThemeSwitcherEnabled` in `nodeRenderer.tsx`).
 * - The public Region Instance's initial `<html class="dark">` state (the
 *   pre-paint boot script, `apps/tenant-region-instance/src/lib/theme-boot-script.ts`).
 *
 * `persistVisitorThemeChoice` ("remember visitor's choice") is deliberately
 * NOT an input to `resolveVisitorThemeModePolicy` — it only affects whether a
 * visitor's pick on the public toggle survives a repeat visit; it never
 * changes which modes are reachable in the first place, so it must never
 * gate builder UI or which modes the boot script considers live.
 *
 * No React here — shared, framework-free logic used by the tenant
 * dashboard's Theme Design form, the builder canvas/inspector, and the
 * public region, so none of them ever disagree on the rule.
 */

export type ThemeColorMode = "light" | "dark" | "system";

export type VisitorThemeModePolicy = {
	/** Visitors can land in either mode — via System, or the visitor switcher. */
	bothModesLive: boolean;
	/** Only light is ever shown to visitors. */
	lightOnly: boolean;
	/** Only dark is ever shown to visitors. */
	darkOnly: boolean;
	/** Canvas/kit mode to force when not `bothModesLive`; `null` when both are live. */
	forcedCanvasMode: "light" | "dark" | null;
	/**
	 * Raw passthrough of the input flag — distinct from `bothModesLive`, which
	 * is ALSO true under `defaultMode: "system"` with the switcher off (OS
	 * preference decides, no visitor-facing control exists). Use this one
	 * specifically to decide whether the `theme_switcher` element should be
	 * enabled/offered — `bothModesLive` would wrongly enable it under plain
	 * System mode too.
	 */
	allowVisitorThemeSwitcher: boolean;
};

const THEME_COLOR_MODE_VALUES = new Set<string>(["light", "dark", "system"]);

/**
 * Coerces arbitrary JSON to a valid `ThemeColorMode`, falling back to
 * `"system"` for anything missing/invalid — matches the Theme Design form's
 * own default so this module and the dashboard never disagree on the
 * fallback for an unset field.
 */
export function normalizeThemeColorMode(value: unknown): ThemeColorMode {
	return typeof value === "string" && THEME_COLOR_MODE_VALUES.has(value)
		? (value as ThemeColorMode)
		: "system";
}

/**
 * `defaultMode` / `allowVisitorThemeSwitcher` → what's actually reachable by
 * a visitor. Missing fields default the same way the Theme Design form does
 * (`defaultMode ?? "system"`, `allowVisitorThemeSwitcher ?? false`), so a
 * fresh tenant with no Theme Design row yet resolves to `bothModesLive`.
 */
export function resolveVisitorThemeModePolicy(input: {
	defaultMode?: ThemeColorMode | null;
	allowVisitorThemeSwitcher?: boolean | null;
}): VisitorThemeModePolicy {
	const defaultMode = input.defaultMode ?? "system";
	const allowVisitorThemeSwitcher = input.allowVisitorThemeSwitcher ?? false;

	const bothModesLive =
		allowVisitorThemeSwitcher === true || defaultMode === "system";
	const lightOnly = defaultMode === "light" && !allowVisitorThemeSwitcher;
	const darkOnly = defaultMode === "dark" && !allowVisitorThemeSwitcher;

	return {
		bothModesLive,
		lightOnly,
		darkOnly,
		forcedCanvasMode: lightOnly ? "light" : darkOnly ? "dark" : null,
		allowVisitorThemeSwitcher,
	};
}

/** Default policy for contexts with no Theme Design data yet — same as `resolveVisitorThemeModePolicy({})`, exported so callers don't need to know that shape to build a safe fallback. */
export const DEFAULT_VISITOR_THEME_MODE_POLICY: VisitorThemeModePolicy =
	resolveVisitorThemeModePolicy({});

/**
 * Public Region Instance contract — shared literal names/keys so the SSR
 * `<html>` attributes (`apps/tenant-region-instance/src/app/layout.tsx`), the
 * inline pre-paint boot script that reads them
 * (`apps/tenant-region-instance/src/lib/theme-boot-script.ts`), and the
 * visitor toggle button that writes the storage key
 * (`packages/builder/runtime/ClassicHeader.tsx`) never drift out of sync.
 * Not used by the builder/dashboard — this half of the module is region-only.
 */
export const VISITOR_THEME_DEFAULT_ATTR = "data-theme-default";
export const VISITOR_THEME_SWITCHER_ATTR = "data-theme-switcher";
export const VISITOR_THEME_PERSIST_ATTR = "data-theme-persist";
/** localStorage key for a visitor's explicit Light|Dark pick — only ever written when both `allowVisitorThemeSwitcher` and `persistVisitorThemeChoice` are on; read the same way, so turning either off makes any leftover value inert. */
export const VISITOR_THEME_CHOICE_STORAGE_KEY = "tt-theme-choice";
